import { useEffect, useState, useCallback } from 'react';
import { orgApi } from '../utils/apiV2';
import { BACKEND_URL } from '../config/api';

/**
 * useSetupProgress
 *
 * Single source of truth for the operator setup checklist. Used by:
 *  - The Help modal's "Setup" home page (primary surface)
 *  - The blue badge on the sidebar Help button (when steps remain)
 *
 * Re-fetches when any "setupDataChanged" event fires (dispatched from
 * AddAssetModal, RolesTab, FlowsTab, WaiversTabClean, PaymentsTabSimple,
 * BrandingTab, WidgetEmbedsSection, AddBookingWizard, CreateServiceWizard,
 * StripeConnectBanner).
 */
export function useSetupProgress({ enabled = true } = {}) {
  const [data, setData] = useState({
    hasRole: false,
    hasTeamMember: false,
    hasAsset: false,
    hasWaiverText: false,
    hasStripeConnect: false,
    hasPaymentProcessor: false,
    processorName: 'Stripe',
    hasService: false,
    hasBooking: false,
    hasFlow: false,
    hasBlog: false,
    stripeIsLive: false,
    orgSlug: null,
    orgId: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const orgRes = await orgApi.getOrganization().catch(() => ({ data: {} }));
      const org = orgRes.data || {};
      const orgId = org.id;

      const [rolesRes, teamRes, assetsRes, servicesRes, bookingsRes, flowsRes, blogsRes] = await Promise.all([
        orgApi.getRoles().catch(() => ({ data: { roles: [] } })),
        orgApi.getTeamMembers().catch(() => ({ data: { teamMembers: [] } })),
        orgApi.getAssets().catch(() => ({ data: { assets: [] } })),
        orgApi.getServices().catch(() => ({ data: { services: [] } })),
        orgApi.getBookings().catch(() => ({ bookings: [] })),
        orgId
          ? fetch(`${BACKEND_URL}/api/flows/org/${orgId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
            }).then((r) => r.json()).catch(() => ({ flows: [] }))
          : Promise.resolve({ flows: [] }),
        orgId
          ? fetch(`${BACKEND_URL}/api/v2/blogs/organizations/${orgId}/blogs`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
            }).then((r) => r.json()).catch(() => ({ blogs: [] }))
          : Promise.resolve({ blogs: [] }),
      ]);

      const roles = rolesRes.data?.roles || rolesRes.data || [];
      const teamData = teamRes.data?.teamMembers || teamRes.data || [];
      const assets = assetsRes.data?.assets || assetsRes.data || [];
      const services = servicesRes.data?.services || servicesRes.data || [];
      const bookings = bookingsRes?.bookings || bookingsRes || [];
      const flows = flowsRes?.flows || [];
      const blogs = blogsRes?.blogs || blogsRes || [];

      const waiverText = org.customWaiverText || org.waiverText || '';
      // Waiver step: real text, waivers live, feature off, OR a signed-off no-waiver decision
      const hasWaiverText = org.waiversFeatureEnabled === false || waiverText.trim().length >= 50 || org.waiversEnabled === true || !!org.waiverDecision;
      const hasStripeConnect = !!(org.stripeConnectAccountId && org.stripeConnectChargesEnabled);
      const ziinaActive = org.paymentProvider === 'ziina' && !!org.ziinaConnected;

      setData({
        hasRole: roles.length > 0,
        hasTeamMember: teamData.length > 0,
        hasAsset: assets.length > 0,
        hasWaiverText,
        hasStripeConnect,
        hasPaymentProcessor: hasStripeConnect || ziinaActive,
        processorName: ziinaActive ? 'Ziina' : 'Stripe',
        hasService: services.length > 0,
        hasBooking: bookings.length > 0,
        hasFlow: flows.length > 0,
        hasBlog: Array.isArray(blogs) && blogs.length > 0,
        stripeIsLive: false, // Live-mode detection TBD
        orgSlug: org.slug,
        orgId: org.id,
      });
    } catch (err) {
      // Silent — fall back to defaults
      console.error('useSetupProgress fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    const handler = () => fetchStatus();
    window.addEventListener('setupDataChanged', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('setupDataChanged', handler);
    };
  }, [enabled, fetchStatus]);

  // Core (non-optional) checklist steps
  const coreSteps = [
    { id: 'asset', complete: data.hasAsset },
    { id: 'role', complete: data.hasRole },
    { id: 'team', complete: data.hasTeamMember },
    { id: 'service', complete: data.hasService },
    { id: 'waiver', complete: data.hasWaiverText },
    { id: 'stripe', complete: data.hasPaymentProcessor },
    { id: 'flow', complete: data.hasFlow },
  ];
  const completedCore = coreSteps.filter((s) => s.complete).length;
  const totalCore = coreSteps.length;
  const remainingCore = totalCore - completedCore;
  const progressPercent = Math.round((completedCore / totalCore) * 100);

  return {
    ...data,
    loading,
    completedCore,
    totalCore,
    remainingCore,
    progressPercent,
    refresh: fetchStatus,
  };
}

export default useSetupProgress;
